/**
 * Emperor Marble stone block grid.
 *
 * Product pages remain Shopify-owned. Live availability is read from the
 * FindMyMarble client and rendered as block cards; block details are fetched
 * only when a shopper opens a card.
 */
(function () {
  'use strict';

  var API_WAIT_MS = 6000;
  var API_WAIT_STEP_MS = 60;
  var areaFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });

  function createElement(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function normalizeName(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function safeNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function formatArea(value) {
    var number = safeNumber(value);
    return number === null ? '—' : areaFormatter.format(number);
  }

  function formatThickness(value) {
    var number = safeNumber(value);
    return number === null ? '—' : areaFormatter.format(number) + ' mm';
  }

  function formatDimensions(block) {
    var length = safeNumber(block.average_length_mm);
    var width = safeNumber(block.average_width_mm);
    if (length === null || width === null) return '—';
    return Math.round(length) + ' × ' + Math.round(width) + ' mm';
  }

  function getSafeMediaUrl(value) {
    if (!value || typeof value !== 'string') return null;
    try {
      var url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
    } catch (_error) {
      return null;
    }
  }

  function collectImages(block) {
    if (!block || typeof block !== 'object') return [];
    var values = [];
    ['images', 'close_up_images', 'book_matched_images'].forEach(function (key) {
      if (Array.isArray(block[key])) values = values.concat(block[key]);
    });

    var seen = new Set();
    return values.reduce(function (result, value) {
      var url = getSafeMediaUrl(value);
      if (url && !seen.has(url)) {
        seen.add(url);
        result.push(url);
      }
      return result;
    }, []);
  }

  function waitForApi() {
    return new Promise(function (resolve, reject) {
      var startedAt = Date.now();
      var timer = window.setInterval(function () {
        if (window.FindMyMarbleAPI) {
          window.clearInterval(timer);
          resolve(window.FindMyMarbleAPI);
          return;
        }
        if (Date.now() - startedAt >= API_WAIT_MS) {
          window.clearInterval(timer);
          reject(new Error('Live inventory client did not initialise'));
        }
      }, API_WAIT_STEP_MS);
    });
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = hidden;
  }

  function syncDocumentDialogLock() {
    document.documentElement.classList.toggle(
      'stone-stock-dialog-open',
      Boolean(document.querySelector('.stone-stock__dialog[open]'))
    );
  }

  function init(root) {
    if (!root || root.dataset.stockGridReady === 'true') return;
    root.dataset.stockGridReady = 'true';

    var stoneName = root.dataset.stoneName || '';
    var productTitle = root.dataset.productTitle || stoneName;
    var showOrigin = root.dataset.showOrigin === 'true';
    var showQuarry = root.dataset.showQuarry === 'true';

    var ui = {
      loading: root.querySelector('[data-stock-loading]'),
      content: root.querySelector('[data-stock-content]'),
      error: root.querySelector('[data-stock-error]'),
      empty: root.querySelector('[data-stock-empty]'),
      grid: root.querySelector('[data-stock-grid]'),
      search: root.querySelector('[data-stock-search]'),
      visibleCount: root.querySelector('[data-stock-visible-count]'),
      blockCount: root.querySelector('[data-stock-block-count]'),
      totalSqm: root.querySelector('[data-stock-total-sqm]'),
      noResults: root.querySelector('[data-stock-no-results]'),
      limitNote: root.querySelector('[data-stock-limit-note]'),
      limitMessage: root.querySelector('[data-stock-limit-message]'),
      detailDialog: root.querySelector('[data-stock-detail-dialog]'),
      detailScroller: root.querySelector('[data-detail-scroller]'),
      detailBackToTop: root.querySelector('[data-detail-back-to-top]'),
      detailGallery: root.querySelector('[data-detail-gallery]'),
      detailGalleryStatus: root.querySelector('[data-detail-gallery-status]'),
      detailSource: root.querySelector('[data-detail-source]'),
      bundleList: root.querySelector('[data-detail-bundles]'),
      bundlePrevious: root.querySelector('[data-bundle-scroll="previous"]'),
      bundleNext: root.querySelector('[data-bundle-scroll="next"]'),
      quoteDialog: root.querySelector('[data-stock-quote-dialog]'),
      quoteForm: root.querySelector('[data-stock-quote-form]'),
      quoteSelection: root.querySelector('[data-quote-selection]'),
      quoteStatus: root.querySelector('[data-quote-status]'),
      quoteSubmit: root.querySelector('[data-quote-submit]'),
      quoteSuccess: root.querySelector('[data-quote-success]'),
      quoteReference: root.querySelector('[data-quote-reference]'),
    };

    var state = {
      api: null,
      blocks: [],
      expectedBlockCount: 0,
      expectedSqm: null,
      query: '',
      sort: 'featured',
      selectedBlock: null,
      selectedBundle: null,
      quoteBlock: null,
      quoteBundle: null,
      detailRequestId: 0,
      galleryImages: [],
      galleryIndex: 0,
      galleryRequestId: 0,
      gallerySourceKey: '',
      failedBundleIds: new Set(),
    };

    function showState(name) {
      setHidden(ui.loading, name !== 'loading');
      setHidden(ui.content, name !== 'content');
      setHidden(ui.error, name !== 'error');
      setHidden(ui.empty, name !== 'empty');
    }

    function getFilteredBlocks() {
      var query = normalizeName(state.query);
      var filtered = state.blocks.filter(function (block) {
        if (!query) return true;
        return [block.block_code, block.stone_name, block.origin_country, block.quarry_name]
          .some(function (value) { return normalizeName(value).includes(query); });
      });

      if (state.sort === 'area') {
        filtered.sort(function (a, b) {
          return (safeNumber(b.available_sqm) || 0) - (safeNumber(a.available_sqm) || 0);
        });
      } else if (state.sort === 'code') {
        filtered.sort(function (a, b) {
          return String(a.block_code || '').localeCompare(String(b.block_code || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });
      } else {
        filtered.sort(function (a, b) { return a.__index - b.__index; });
      }

      return filtered;
    }

    function buildPlaceholder() {
      var placeholder = createElement('div', 'stone-stock__image-placeholder');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.appendChild(createElement('span', '', 'EM'));
      return placeholder;
    }

    function buildCard(block, index) {
      var card = createElement('article', 'stone-block-card');
      card.id = 'block-' + block.id;
      card.dataset.blockId = block.id;
      card.style.animationDelay = Math.min(index, 8) * 45 + 'ms';

      var images = collectImages(block);
      var mediaButton = createElement('button', 'stone-block-card__media');
      mediaButton.type = 'button';
      mediaButton.dataset.stockAction = 'view';
      mediaButton.dataset.blockId = block.id;
      mediaButton.setAttribute('aria-label', 'View details for block ' + (block.block_code || ''));

      var placeholder = buildPlaceholder();
      mediaButton.appendChild(placeholder);

      if (images.length > 0) {
        var image = createElement('img');
        image.className = 'stone-block-card__image';
        image.alt = (block.stone_name || productTitle) + ' block ' + (block.block_code || '');
        image.loading = 'lazy';
        image.decoding = 'async';
        mediaButton.setAttribute('aria-busy', 'true');

        var imageIndex = 0;
        var imageCount = null;
        function tryNextCardImage() {
          if (imageIndex >= images.length) {
            image.remove();
            if (imageCount) imageCount.remove();
            placeholder.hidden = false;
            mediaButton.removeAttribute('aria-busy');
            return;
          }
          image.classList.remove('is-loaded');
          placeholder.hidden = false;
          image.src = images[imageIndex];
          imageIndex += 1;
        }
        image.addEventListener('load', function () {
          image.classList.add('is-loaded');
          placeholder.hidden = true;
          mediaButton.removeAttribute('aria-busy');
        });
        image.addEventListener('error', tryNextCardImage);
        tryNextCardImage();
        mediaButton.appendChild(image);

        if (images.length > 1) {
          imageCount = createElement('span', 'stone-block-card__image-count', String(images.length));
          mediaButton.appendChild(imageCount);
        }
      }

      card.appendChild(mediaButton);

      var body = createElement('div', 'stone-block-card__body');
      var topLine = createElement('div', 'stone-block-card__topline');
      topLine.appendChild(createElement('p', 'stone-block-card__code', block.block_code || 'Uncoded block'));
      topLine.appendChild(createElement('p', 'stone-block-card__status', 'Available'));
      body.appendChild(topLine);
      body.appendChild(createElement('h3', 'stone-block-card__title', block.stone_name || productTitle));

      var originParts = [];
      if (showOrigin && block.origin_country) originParts.push(block.origin_country);
      if (showQuarry && block.quarry_name) originParts.push(block.quarry_name);
      body.appendChild(createElement('p', 'stone-block-card__origin', originParts.join(' · ') || '\u00a0'));

      var metrics = createElement('dl', 'stone-block-card__metrics');
      [
        ['Available', formatArea(block.available_sqm) + (safeNumber(block.available_sqm) === null ? '' : ' m²')],
        ['Thickness', formatThickness(block.thickness_mm)],
        ['Average size', formatDimensions(block)],
      ].forEach(function (metric) {
        var group = createElement('div');
        group.appendChild(createElement('dt', '', metric[0]));
        group.appendChild(createElement('dd', '', metric[1]));
        metrics.appendChild(group);
      });
      body.appendChild(metrics);

      var actions = createElement('div', 'stone-block-card__actions');
      var viewButton = createElement('button', 'stone-block-card__view', 'View block');
      viewButton.type = 'button';
      viewButton.dataset.stockAction = 'view';
      viewButton.dataset.blockId = block.id;
      var quoteButton = createElement('button', 'stone-block-card__quote', 'Quote');
      quoteButton.type = 'button';
      quoteButton.dataset.stockAction = 'quote';
      quoteButton.dataset.blockId = block.id;
      actions.appendChild(viewButton);
      actions.appendChild(quoteButton);
      body.appendChild(actions);
      card.appendChild(body);

      return card;
    }

    function renderGrid() {
      var blocks = getFilteredBlocks();
      var fragment = document.createDocumentFragment();
      blocks.forEach(function (block, index) {
        fragment.appendChild(buildCard(block, index));
      });
      ui.grid.replaceChildren(fragment);

      if (ui.visibleCount) ui.visibleCount.textContent = String(blocks.length);
      setHidden(ui.noResults, blocks.length !== 0 || !state.query);
      ui.grid.hidden = blocks.length === 0;
    }

    function updateSummary() {
      var loadedSqm = state.blocks.reduce(function (sum, block) {
        return sum + (safeNumber(block.available_sqm) || 0);
      }, 0);
      var totalSqm = state.expectedSqm === null ? loadedSqm : state.expectedSqm;
      var totalBlocks = state.expectedBlockCount || state.blocks.length;
      if (ui.blockCount) ui.blockCount.textContent = String(totalBlocks);
      if (ui.totalSqm) ui.totalSqm.textContent = formatArea(totalSqm);

      var limited = totalBlocks > state.blocks.length;
      setHidden(ui.limitNote, !limited);
      if (limited && ui.limitMessage) {
        ui.limitMessage.textContent = 'Showing ' + state.blocks.length + ' of ' + totalBlocks + ' live blocks. The complete list is available on request.';
      }
    }

    async function loadInventory() {
      showState('loading');
      try {
        state.api = await waitForApi();
        var responses = await Promise.all([
          state.api.getBlocks(stoneName),
          state.api.getStoneTypes().catch(function () { return null; }),
        ]);
        var blocksResponse = responses[0];
        var stoneTypesResponse = responses[1];

        if (!blocksResponse || blocksResponse.error) {
          throw new Error(blocksResponse && blocksResponse.message ? blocksResponse.message : 'Blocks request failed');
        }

        var exactStoneName = normalizeName(stoneName);
        state.blocks = (blocksResponse.blocks || [])
          .filter(function (block) { return normalizeName(block.stone_name) === exactStoneName; })
          .map(function (block, index) {
            block.__index = index;
            return block;
          });

        var stoneType = stoneTypesResponse && Array.isArray(stoneTypesResponse.stone_types)
          ? stoneTypesResponse.stone_types.find(function (item) {
              return normalizeName(item.stone_name) === exactStoneName;
            })
          : null;

        state.expectedBlockCount = stoneType ? Number(stoneType.block_count) || 0 : state.blocks.length;
        state.expectedSqm = stoneType ? safeNumber(stoneType.total_available_sqm) : null;
        updateSummary();

        if (state.blocks.length === 0) {
          showState(state.expectedBlockCount > 0 ? 'error' : 'empty');
          return;
        }

        renderGrid();
        showState('content');
        revealHashBlock();
      } catch (error) {
        console.warn('stone-block-grid: ' + (error && error.message ? error.message : error));
        showState('error');
      }
    }

    function getBlock(blockId) {
      return state.blocks.find(function (block) { return block.id === blockId; }) || null;
    }

    function getBundleId(bundle) {
      return bundle && bundle.id ? String(bundle.id) : '';
    }

    function getBundleLabel(bundle) {
      return 'Bundle ' + (bundle && bundle.bundle_number ? bundle.bundle_number : '—');
    }

    function getGallerySourceMeta() {
      var block = state.selectedBlock || {};
      var stone = productTitle || block.stone_name;
      if (state.selectedBundle) {
        var bundleLabel = getBundleLabel(state.selectedBundle);
        return {
          key: 'bundle:' + getBundleId(state.selectedBundle),
          label: bundleLabel,
          altPrefix: stone + ', ' + bundleLabel,
          chip: bundleLabel,
        };
      }

      var blockCode = block.block_code || '—';
      return {
        key: 'block:' + (block.id || blockCode),
        label: 'block ' + blockCode,
        altPrefix: stone + ' block ' + blockCode,
        chip: '',
      };
    }

    function updateGallerySourceUi(imageCount) {
      var source = getGallerySourceMeta();
      if (ui.detailGallery) ui.detailGallery.setAttribute('aria-label', source.label + ' gallery');
      if (ui.detailSource) {
        ui.detailSource.textContent = source.chip;
        ui.detailSource.hidden = !source.chip;
      }
      if (ui.detailGalleryStatus && state.gallerySourceKey !== source.key) {
        ui.detailGalleryStatus.textContent = 'Showing ' + source.label + ' photos, ' + imageCount + (imageCount === 1 ? ' image.' : ' images.');
      }
      state.gallerySourceKey = source.key;
      return source;
    }

    function setDetailImage(index) {
      if (state.galleryImages.length === 0) return;
      var requestId = ++state.galleryRequestId;
      var activeBundle = state.selectedBundle;
      state.galleryIndex = (index + state.galleryImages.length) % state.galleryImages.length;
      var image = ui.detailDialog.querySelector('[data-detail-image]');
      var placeholder = ui.detailDialog.querySelector('[data-detail-placeholder]');
      var counter = ui.detailDialog.querySelector('[data-detail-image-counter]');
      var attempts = 0;

      function updateGalleryState() {
        var source = getGallerySourceMeta();
        image.alt = source.altPrefix + ' — image ' + (state.galleryIndex + 1) + ' of ' + state.galleryImages.length;
        counter.textContent = (state.galleryIndex + 1) + ' / ' + state.galleryImages.length;
        counter.hidden = state.galleryImages.length < 2;
        ui.detailDialog.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb, thumbIndex) {
          thumb.classList.toggle('is-active', thumbIndex === state.galleryIndex);
          thumb.setAttribute('aria-current', thumbIndex === state.galleryIndex ? 'true' : 'false');
        });
      }

      function loadCurrentImage() {
        attempts += 1;
        image.hidden = true;
        placeholder.hidden = false;
        updateGalleryState();
        image.src = state.galleryImages[state.galleryIndex];
      }

      image.onload = function () {
        if (requestId !== state.galleryRequestId) return;
        image.hidden = false;
        placeholder.hidden = true;
      };
      image.onerror = function () {
        if (requestId !== state.galleryRequestId) return;
        if (attempts < state.galleryImages.length) {
          state.galleryIndex = (state.galleryIndex + 1) % state.galleryImages.length;
          loadCurrentImage();
          return;
        }
        image.hidden = true;
        placeholder.hidden = false;
        if (activeBundle && getBundleId(activeBundle) === getBundleId(state.selectedBundle)) {
          markBundleMediaUnavailable(activeBundle);
        }
      };
      loadCurrentImage();
    }

    function renderDetailGallery(source) {
      state.galleryRequestId += 1;
      state.galleryImages = collectImages(source);
      state.galleryIndex = 0;
      var image = ui.detailDialog.querySelector('[data-detail-image]');
      var placeholder = ui.detailDialog.querySelector('[data-detail-placeholder]');
      var counter = ui.detailDialog.querySelector('[data-detail-image-counter]');
      var thumbs = ui.detailDialog.querySelector('[data-detail-thumbs]');
      var previous = ui.detailDialog.querySelector('[data-gallery-prev]');
      var next = ui.detailDialog.querySelector('[data-gallery-next]');
      var gallerySource = updateGallerySourceUi(state.galleryImages.length);
      image.onload = null;
      image.onerror = null;
      thumbs.replaceChildren();
      thumbs.hidden = state.galleryImages.length < 2;
      thumbs.closest('[data-detail-stage]').classList.toggle('has-thumbnail-tray', state.galleryImages.length >= 2);
      thumbs.setAttribute('aria-label', gallerySource.label + ' images');

      if (state.galleryImages.length === 0) {
        image.hidden = true;
        image.removeAttribute('src');
        placeholder.hidden = false;
        counter.hidden = true;
        previous.hidden = true;
        next.hidden = true;
        return;
      }

      state.galleryImages.forEach(function (url, index) {
        var button = createElement('button');
        button.type = 'button';
        button.dataset.galleryThumb = String(index);
        button.setAttribute('aria-label', 'View ' + gallerySource.label + ' image ' + (index + 1) + ' of ' + state.galleryImages.length);
        var thumbImage = createElement('img');
        thumbImage.src = url;
        thumbImage.alt = '';
        thumbImage.loading = 'lazy';
        button.appendChild(thumbImage);
        thumbs.appendChild(button);
      });
      previous.hidden = state.galleryImages.length < 2;
      next.hidden = state.galleryImages.length < 2;
      setDetailImage(0);
    }

    function renderActiveGallery() {
      renderDetailGallery(state.selectedBundle || state.selectedBlock);
      syncBundleSelectionUi();
    }

    function syncBundleSelectionUi() {
      var activeBundleId = getBundleId(state.selectedBundle);
      ui.detailDialog.querySelectorAll('[data-bundle-select]').forEach(function (button) {
        var isActive = Boolean(activeBundleId) && button.dataset.bundleSelect === activeBundleId;
        var row = button.closest('.stone-stock__bundle-row');
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (row) row.classList.toggle('is-active', isActive);
        var desktopState = button.querySelector('[data-bundle-state-desktop]');
        var mobileState = button.querySelector('[data-bundle-state-mobile]');
        if (desktopState) desktopState.textContent = isActive ? 'Showing photos' : 'View photos';
        if (mobileState) mobileState.textContent = isActive ? 'Showing above' : 'View photos';
      });
    }

    function toggleBundleGallery(bundle) {
      var bundleId = getBundleId(bundle);
      if (!bundleId || state.failedBundleIds.has(bundleId) || collectImages(bundle).length === 0) return;
      state.selectedBundle = getBundleId(state.selectedBundle) === bundleId ? null : bundle;
      renderActiveGallery();
    }

    function createBundleCopy(bundle, showViewState, unavailableText) {
      var copy = createElement('span', 'stone-stock__bundle-copy');
      var slabCount = bundle.slab_count || 0;
      var slabLabel = slabCount + (Number(slabCount) === 1 ? ' slab' : ' slabs');
      copy.appendChild(createElement('strong', '', getBundleLabel(bundle)));
      copy.appendChild(createElement('span', 'stone-stock__bundle-meta', (bundle.finish || 'Finish on request') + ' · ' + slabLabel));
      if (showViewState) {
        var desktopState = createElement('span', 'stone-stock__bundle-view-state stone-stock__bundle-view-state--desktop', 'View photos');
        var mobileState = createElement('span', 'stone-stock__bundle-view-state stone-stock__bundle-view-state--mobile', 'View photos');
        desktopState.setAttribute('data-bundle-state-desktop', '');
        mobileState.setAttribute('data-bundle-state-mobile', '');
        desktopState.setAttribute('aria-hidden', 'true');
        mobileState.setAttribute('aria-hidden', 'true');
        copy.appendChild(desktopState);
        copy.appendChild(mobileState);
      } else if (unavailableText) {
        copy.appendChild(createElement('span', 'stone-stock__bundle-view-state', unavailableText));
      }
      return copy;
    }

    function createBundleRequestButton(bundle) {
      var button = createElement('button', 'stone-stock__bundle-request', 'Request');
      button.type = 'button';
      button.dataset.bundleQuote = getBundleId(bundle);
      button.setAttribute('aria-label', 'Request ' + getBundleLabel(bundle));
      button.__bundleData = bundle;
      return button;
    }

    function createBundleStatic(bundle, message) {
      var content = createElement('div', 'stone-stock__bundle-static');
      content.appendChild(createBundleCopy(bundle, false, message));
      return content;
    }

    function loadBundleThumbnail(image, images, onAllFailed) {
      var index = 0;
      function tryNext() {
        if (index >= images.length) {
          onAllFailed();
          return;
        }
        image.classList.remove('is-loaded');
        image.src = images[index];
        index += 1;
      }
      image.addEventListener('load', function () { image.classList.add('is-loaded'); });
      image.addEventListener('error', tryNext);
      tryNext();
    }

    function findBundleRow(bundleId) {
      var rows = ui.detailDialog.querySelectorAll('.stone-stock__bundle-row');
      return Array.prototype.find.call(rows, function (row) {
        return getBundleId(row.__bundleData) === bundleId;
      }) || null;
    }

    function demoteBundlePreview(bundle) {
      var row = findBundleRow(getBundleId(bundle));
      if (!row) return;
      var preview = row.querySelector('[data-bundle-select]');
      if (!preview) return;
      var restoreFocus = preview.contains(document.activeElement);
      preview.replaceWith(createBundleStatic(bundle, 'Photos unavailable'));
      row.classList.remove('has-media', 'is-active');
      row.classList.add('is-media-unavailable');
      if (restoreFocus) {
        var requestButton = row.querySelector('[data-bundle-quote]');
        if (requestButton) requestButton.focus();
      }
    }

    function markBundleMediaUnavailable(bundle) {
      var bundleId = getBundleId(bundle);
      if (!bundleId) return;
      state.failedBundleIds.add(bundleId);
      demoteBundlePreview(bundle);
      if (getBundleId(state.selectedBundle) === bundleId) {
        state.selectedBundle = null;
        renderActiveGallery();
        return;
      }
      syncBundleSelectionUi();
    }

    function createBundlePreview(bundle, images) {
      var button = createElement('button', 'stone-stock__bundle-preview');
      button.type = 'button';
      button.dataset.bundleSelect = getBundleId(bundle);
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', getBundleLabel(bundle) + ' photos for ' + (productTitle || state.selectedBlock.stone_name) + ' block ' + (state.selectedBlock.block_code || '—'));
      if (ui.detailGallery && ui.detailGallery.id) button.setAttribute('aria-controls', ui.detailGallery.id);
      button.__bundleData = bundle;

      var thumbnail = createElement('span', 'stone-stock__bundle-thumb');
      var image = createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      thumbnail.appendChild(image);
      if (images.length > 1) {
        var imageCount = createElement('span', 'stone-stock__bundle-image-count', String(images.length));
        imageCount.setAttribute('aria-hidden', 'true');
        thumbnail.appendChild(imageCount);
      }
      button.appendChild(thumbnail);
      button.appendChild(createBundleCopy(bundle, true, ''));
      loadBundleThumbnail(image, images, function () { markBundleMediaUnavailable(bundle); });
      return button;
    }

    function prefersReducedMotion() {
      return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    function syncBundleScrollControls() {
      var list = ui.bundleList;
      if (!list || !ui.bundlePrevious || !ui.bundleNext) return;
      var maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      var hasOverflow = maxScroll > 2;
      var showPrevious = hasOverflow && list.scrollLeft > 2;
      var showNext = hasOverflow && list.scrollLeft < maxScroll - 2;
      var activeElement = document.activeElement;

      if (activeElement === ui.bundlePrevious && !showPrevious) {
        (showNext ? ui.bundleNext : list).focus({ preventScroll: true });
      } else if (activeElement === ui.bundleNext && !showNext) {
        (showPrevious ? ui.bundlePrevious : list).focus({ preventScroll: true });
      }

      ui.bundlePrevious.hidden = !showPrevious;
      ui.bundleNext.hidden = !showNext;
    }

    function scheduleBundleScrollSync() {
      window.requestAnimationFrame(syncBundleScrollControls);
    }

    function scrollBundleRail(direction) {
      var list = ui.bundleList;
      if (!list) return;
      var firstCard = list.querySelector('.stone-stock__bundle-row');
      var gap = Number.parseFloat(window.getComputedStyle(list).gap) || 0;
      var distance = firstCard ? firstCard.getBoundingClientRect().width + gap : list.clientWidth * 0.8;
      list.scrollBy({
        left: direction === 'previous' ? -distance : distance,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    function syncDetailBackToTop() {
      if (!ui.detailScroller || !ui.detailBackToTop) return;
      var maxScroll = Math.max(0, ui.detailScroller.scrollHeight - ui.detailScroller.clientHeight);
      var threshold = Math.min(160, Math.max(48, maxScroll * 0.35));
      ui.detailBackToTop.hidden = maxScroll <= 2 || ui.detailScroller.scrollTop < threshold;
    }

    function scrollDetailToTop() {
      if (!ui.detailScroller) return;
      var closeButton = ui.detailDialog.querySelector('[data-close-dialog]');
      if (closeButton) closeButton.focus({ preventScroll: true });
      ui.detailScroller.scrollTo({
        top: 0,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    function renderBundles(bundles) {
      var list = ui.bundleList;
      var count = ui.detailDialog.querySelector('[data-detail-bundle-count]');
      var loading = ui.detailDialog.querySelector('[data-detail-loading]');
      list.scrollLeft = 0;
      list.replaceChildren();
      loading.hidden = true;
      count.textContent = bundles.length + (bundles.length === 1 ? ' bundle' : ' bundles');

      if (bundles.length === 0) {
        list.appendChild(createElement('li', 'stone-stock__bundle-loading', 'Bundle details are available on request.'));
        scheduleBundleScrollSync();
        return;
      }

      bundles.forEach(function (bundle) {
        var bundleId = getBundleId(bundle);
        var images = state.failedBundleIds.has(bundleId) ? [] : collectImages(bundle);
        var hasMedia = Boolean(bundleId) && images.length > 0;
        var row = createElement('li', 'stone-stock__bundle-row ' + (hasMedia ? 'has-media' : 'is-media-unavailable'));
        row.__bundleData = bundle;
        row.appendChild(hasMedia ? createBundlePreview(bundle, images) : createBundleStatic(bundle, 'Photos unavailable'));
        row.appendChild(createBundleRequestButton(bundle));
        list.appendChild(row);
      });
      syncBundleSelectionUi();
      scheduleBundleScrollSync();
    }

    async function openDetail(block) {
      if (!block) return;
      state.selectedBlock = block;
      state.selectedBundle = null;
      state.detailRequestId += 1;
      var requestId = state.detailRequestId;

      ui.detailDialog.querySelector('[data-detail-code]').textContent = block.block_code || 'Uncoded block';
      ui.detailDialog.querySelector('[data-detail-title]').textContent = block.stone_name || productTitle;
      var originParts = [block.origin_country, block.quarry_name].filter(Boolean);
      ui.detailDialog.querySelector('[data-detail-origin]').textContent = originParts.join(' · ');
      ui.detailDialog.querySelector('[data-detail-sqm]').textContent = formatArea(block.available_sqm) + (safeNumber(block.available_sqm) === null ? '' : ' m²');
      ui.detailDialog.querySelector('[data-detail-thickness]').textContent = formatThickness(block.thickness_mm);
      ui.detailDialog.querySelector('[data-detail-dimensions]').textContent = formatDimensions(block);
      ui.bundleList.replaceChildren();
      ui.detailDialog.querySelector('[data-detail-bundle-count]').textContent = '';
      ui.detailDialog.querySelector('[data-detail-loading]').hidden = false;
      if (ui.detailScroller) ui.detailScroller.scrollTop = 0;
      syncDetailBackToTop();
      scheduleBundleScrollSync();
      renderDetailGallery(block);
      if (!ui.detailDialog.open) ui.detailDialog.showModal();
      syncDocumentDialogLock();
      window.requestAnimationFrame(syncDetailBackToTop);

      try {
        var response = await state.api.getBlockDetail(block.id);
        if (requestId !== state.detailRequestId || !ui.detailDialog.open) return;
        if (response && !response.error && response.block) {
          state.selectedBlock = Object.assign({}, block, response.block);
          renderDetailGallery(state.selectedBlock);
          renderBundles(Array.isArray(response.block.bundles) ? response.block.bundles : []);
        } else {
          renderBundles([]);
        }
      } catch (_error) {
        if (requestId === state.detailRequestId) renderBundles([]);
      }
    }

    function openQuote(block, bundle) {
      if (!block) return;
      state.quoteBlock = block;
      state.quoteBundle = bundle || null;
      if (ui.detailDialog.open) ui.detailDialog.close();

      ui.quoteForm.reset();
      ui.quoteForm.hidden = false;
      ui.quoteSuccess.hidden = true;
      ui.quoteStatus.textContent = '';
      ui.quoteSubmit.disabled = false;
      ui.quoteSubmit.classList.remove('is-loading');

      var selection = 'Block ' + (block.block_code || '—') + ' · ' + (block.stone_name || productTitle);
      if (bundle) selection += ' · Bundle ' + (bundle.bundle_number || '—');
      ui.quoteSelection.textContent = selection;
      if (!ui.quoteDialog.open) ui.quoteDialog.showModal();
      syncDocumentDialogLock();
    }

    async function submitQuote(event) {
      event.preventDefault();
      if (!state.quoteBlock || !ui.quoteForm.reportValidity()) return;

      var formData = new FormData(ui.quoteForm);
      var quantity = Number(formData.get('quantity'));
      if (!Number.isFinite(quantity) || quantity <= 0) {
        ui.quoteStatus.textContent = 'Enter a valid quantity.';
        return;
      }

      ui.quoteStatus.textContent = '';
      ui.quoteSubmit.disabled = true;
      ui.quoteSubmit.classList.add('is-loading');

      var block = state.quoteBlock;
      var bundle = state.quoteBundle;
      var payload = {
        stone_name: block.stone_name || stoneName,
        block_id: block.id,
        block_code: block.block_code || '',
        stone_category: block.stone_category || '',
        quantity: quantity,
        unit: String(formData.get('unit') || 'sqm'),
        product_dimensions: formatDimensions(block),
        customer: {
          company_name: String(formData.get('company') || '').trim(),
          email: String(formData.get('email') || '').trim(),
          phone: String(formData.get('phone') || '').trim(),
          notes: String(formData.get('notes') || '').trim(),
        },
      };

      if (bundle) {
        payload.bundle_id = bundle.id;
        payload.bundle_number = bundle.bundle_number;
        payload.bundle_finish = bundle.finish || '';
      }

      try {
        var result = await state.api.submitQuote(payload);
        if (!result || result.error || result.success === false) {
          throw new Error(result && result.message ? result.message : 'Request could not be submitted');
        }
        ui.quoteForm.hidden = true;
        ui.quoteSuccess.hidden = false;
        ui.quoteReference.textContent = result.reference_number ? 'Reference ' + result.reference_number : '';
      } catch (error) {
        ui.quoteStatus.textContent = error && error.message ? error.message : 'Request could not be submitted. Please try again.';
      } finally {
        ui.quoteSubmit.disabled = false;
        ui.quoteSubmit.classList.remove('is-loading');
      }
    }

    function closeDialog(button) {
      var dialog = button.closest('dialog');
      if (dialog) dialog.close();
    }

    function resetDetailVisualState() {
      state.detailRequestId += 1;
      state.galleryRequestId += 1;
      state.selectedBundle = null;
      state.galleryImages = [];
      state.galleryIndex = 0;
      state.gallerySourceKey = '';
      if (ui.detailSource) {
        ui.detailSource.textContent = '';
        ui.detailSource.hidden = true;
      }
      if (ui.detailGalleryStatus) ui.detailGalleryStatus.textContent = '';
      if (ui.detailScroller) ui.detailScroller.scrollTop = 0;
      if (ui.detailBackToTop) ui.detailBackToTop.hidden = true;
      if (ui.bundleList) ui.bundleList.scrollLeft = 0;
      scheduleBundleScrollSync();
      syncBundleSelectionUi();
    }

    function resetQuoteState() {
      state.quoteBlock = null;
      state.quoteBundle = null;
    }

    function revealHashBlock() {
      var hash = window.location.hash;
      if (!hash || !hash.startsWith('#block-')) return;
      var id = hash.slice('#block-'.length);
      var card = document.getElementById('block-' + id);
      if (!card) return;
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      card.classList.add('is-highlighted');
      window.setTimeout(function () { card.classList.remove('is-highlighted'); }, 1800);
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      var actionButton = target.closest('[data-stock-action]');
      if (actionButton) {
        var block = getBlock(actionButton.dataset.blockId);
        if (actionButton.dataset.stockAction === 'view') openDetail(block);
        if (actionButton.dataset.stockAction === 'quote') openQuote(block, null);
        return;
      }

      var sortButton = target.closest('[data-stock-sort]');
      if (sortButton) {
        state.sort = sortButton.dataset.stockSort;
        root.querySelectorAll('[data-stock-sort]').forEach(function (button) {
          var active = button === sortButton;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        renderGrid();
        return;
      }

      if (target.closest('[data-stock-retry]')) {
        loadInventory();
        return;
      }

      if (target.closest('[data-stock-clear-search]')) {
        state.query = '';
        if (ui.search) ui.search.value = '';
        renderGrid();
        return;
      }

      var closeButton = target.closest('[data-close-dialog]');
      if (closeButton) {
        closeDialog(closeButton);
        return;
      }

      if (target.closest('[data-gallery-prev]')) {
        setDetailImage(state.galleryIndex - 1);
        return;
      }

      if (target.closest('[data-gallery-next]')) {
        setDetailImage(state.galleryIndex + 1);
        return;
      }

      var thumb = target.closest('[data-gallery-thumb]');
      if (thumb) {
        setDetailImage(Number(thumb.dataset.galleryThumb));
        return;
      }

      var bundleScroll = target.closest('[data-bundle-scroll]');
      if (bundleScroll) {
        scrollBundleRail(bundleScroll.dataset.bundleScroll);
        return;
      }

      if (target.closest('[data-detail-back-to-top]')) {
        scrollDetailToTop();
        return;
      }

      var bundleQuote = target.closest('[data-bundle-quote]');
      if (bundleQuote) {
        openQuote(state.selectedBlock, bundleQuote.__bundleData || null);
        return;
      }

      var bundleSelect = target.closest('[data-bundle-select]');
      if (bundleSelect) {
        toggleBundleGallery(bundleSelect.__bundleData || null);
        return;
      }

      if (target.closest('[data-detail-quote]')) {
        openQuote(state.selectedBlock, null);
      }
    });

    if (ui.search) {
      ui.search.addEventListener('input', function () {
        state.query = ui.search.value;
        renderGrid();
      });
    }

    if (ui.bundleList) {
      ui.bundleList.addEventListener('scroll', scheduleBundleScrollSync, { passive: true });
    }

    if (ui.detailScroller) {
      ui.detailScroller.addEventListener('scroll', syncDetailBackToTop, { passive: true });
    }

    window.addEventListener('resize', function () {
      scheduleBundleScrollSync();
      syncDetailBackToTop();
    });

    if (ui.quoteForm) ui.quoteForm.addEventListener('submit', submitQuote);

    [ui.detailDialog, ui.quoteDialog].forEach(function (dialog) {
      if (!dialog) return;
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', function () {
        window.requestAnimationFrame(syncDocumentDialogLock);
      });
    });

    if (ui.detailDialog) ui.detailDialog.addEventListener('close', resetDetailVisualState);
    if (ui.quoteDialog) ui.quoteDialog.addEventListener('close', resetQuoteState);

    scheduleBundleScrollSync();
    syncDetailBackToTop();
    loadInventory();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-stone-block-grid]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    window.requestAnimationFrame(syncDocumentDialogLock);
  });

  document.addEventListener('shopify:section:unload', function () {
    window.requestAnimationFrame(syncDocumentDialogLock);
  });

  window.addEventListener('pageshow', syncDocumentDialogLock);
})();
